<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTicketsStore } from 'dashboard/stores/tickets';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import TicketDetail from './TicketDetail.vue';
import TicketStatusBadge from './TicketStatusBadge.vue';
import { formatTicketDateTime } from './formatters';

const emit = defineEmits(['newTicket']);

const STATUS_TABS = ['open', 'closed'];

const { t, locale } = useI18n();
const store = useTicketsStore();

const dialogRef = ref(null);
const activeTab = ref(0);
const selectedTicketId = ref(null);

const tabs = computed(() => [
  { label: t('TICKETS.LIST.OPEN_TAB') },
  { label: t('TICKETS.LIST.CLOSED_TAB') },
]);

const hasMorePages = computed(
  () => store.meta.currentPage < store.meta.totalPages
);

const loadTickets = (page = 1) =>
  store.fetchTickets({ status: STATUS_TABS[activeTab.value], page });

const open = (ticketId = null) => {
  selectedTicketId.value = ticketId;
  activeTab.value = 0;
  dialogRef.value.open();
  loadTickets();
};

const onTabChanged = tab => {
  activeTab.value = tabs.value.indexOf(tab);
  loadTickets();
};

const backToList = () => {
  selectedTicketId.value = null;
  loadTickets();
};

const startNewTicket = () => {
  dialogRef.value.close();
  emit('newTicket');
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    overflow-y-auto
    :title="t('TICKETS.LIST.TITLE')"
    :description="t('TICKETS.LIST.DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
  >
    <TicketDetail
      v-if="selectedTicketId"
      :ticket-id="selectedTicketId"
      @back="backToList"
    />
    <div v-else class="flex flex-col gap-4">
      <div class="flex items-center justify-between gap-2">
        <TabBar
          :tabs="tabs"
          :initial-active-tab="activeTab"
          @tab-changed="onTabChanged"
        />
        <Button
          type="button"
          size="sm"
          icon="i-lucide-ticket-plus"
          :label="t('TICKETS.LIST.NEW_BUTTON')"
          @click="startNewTicket"
        />
      </div>
      <p
        v-if="!store.records.length && !store.uiFlags.isFetching"
        class="py-8 m-0 text-sm text-center text-n-slate-11"
      >
        {{ t('TICKETS.LIST.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col m-0 list-none divide-y divide-n-weak">
        <li v-for="ticket in store.records" :key="ticket.id">
          <button
            type="button"
            class="flex items-center w-full gap-3 px-2 py-3 text-start rounded-lg hover:bg-n-alpha-1"
            @click="selectedTicketId = ticket.id"
          >
            <span
              class="flex-shrink-0 rounded-full size-2"
              :class="ticket.unread ? 'bg-n-blue-9' : 'bg-transparent'"
              :title="ticket.unread ? t('TICKETS.LIST.UNREAD') : undefined"
            />
            <span class="flex flex-col flex-1 min-w-0 gap-0.5">
              <span
                class="text-sm truncate text-n-slate-12"
                :class="{ 'font-semibold': ticket.unread }"
              >
                {{ ticket.title }}
              </span>
              <span class="text-xs truncate text-n-slate-11">
                {{ ticket.displayId }} · {{ ticket.ticketType }} ·
                {{ ticket.requesterName }} ·
                {{
                  t('TICKETS.LIST.UPDATED_AT', {
                    time: formatTicketDateTime(ticket.lastActivityAt, locale),
                  })
                }}
              </span>
            </span>
            <TicketStatusBadge :status="ticket.status" />
          </button>
        </li>
      </ul>
      <Button
        v-if="hasMorePages"
        type="button"
        variant="faded"
        color="slate"
        size="sm"
        class="self-center"
        :label="t('TICKETS.LIST.LOAD_MORE')"
        :is-loading="store.uiFlags.isFetching"
        @click="loadTickets(store.meta.currentPage + 1)"
      />
    </div>
  </Dialog>
</template>
